import { useCallback, useEffect, useRef, useState } from 'react';
import useGetSettingsByEventId, { type EventSettings } from '@features/api/use-get-settings';
import {
  findTimeSlotFromDate,
  OUTLOOK_MEETING_DEFAULT_END_TIME,
  OUTLOOK_MEETING_DEFAULT_START_TIME,
  OUTLOOK_TIME_SLOT_OPTIONS,
  type OutlookTimeSlotOption,
} from '@features/outlook-meeting';
import { formatLocalDateToUtc } from '@shared/libs/format-local-date';

const defaultStart = () =>
  OUTLOOK_TIME_SLOT_OPTIONS.find((slot) => slot.value === OUTLOOK_MEETING_DEFAULT_START_TIME) ?? null;
const defaultEnd = () =>
  OUTLOOK_TIME_SLOT_OPTIONS.find((slot) => slot.value === OUTLOOK_MEETING_DEFAULT_END_TIME) ?? null;

export const useTicketOutlookMeeting = (eventId: number, eventDateStart: string | null) => {
  const { data, isLoading, error: loadError } = useGetSettingsByEventId(eventId);
  const saved = data as EventSettings | undefined;
  const [enabled, setEnabled] = useState(false);
  const [start, setStart] = useState<OutlookTimeSlotOption | null>(defaultStart);
  const [end, setEnd] = useState<OutlookTimeSlotOption | null>(defaultEnd);
  const [location, setLocation] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [savedDate, setSavedDate] = useState<string | null>(null);
  const hydrated = useRef(false);
  const dirty = useRef(false);
  const currentEvent = useRef(eventId);
  const readOnly = saved?.eventId === eventId && !!saved.calendarMeetingId;

  useEffect(() => {
    if (currentEvent.current !== eventId) {
      currentEvent.current = eventId;
      hydrated.current = false;
      dirty.current = false;
      setEnabled(false);
      setStart(defaultStart());
      setEnd(defaultEnd());
      setLocation('');
      setComment('');
      setSavedDate(null);
      setError('');
    }
    if (!saved || saved.eventId !== eventId || (!readOnly && (hydrated.current || dirty.current))) return;
    hydrated.current = true;
    setStart(findTimeSlotFromDate(saved.calendarMeetingDateStart) ?? defaultStart());
    setEnd(findTimeSlotFromDate(saved.calendarMeetingDateEnd) ?? defaultEnd());
    setLocation(saved.calendarMeetingPoint ?? '');
    setComment(saved.calendarMeetingDescription ?? '');
    setSavedDate(saved.calendarMeetingDateStart);
  }, [eventId, saved, readOnly]);

  const date = readOnly && savedDate ? new Date(savedDate + 'Z') : eventDateStart ? new Date(eventDateStart) : null;
  const onChange =
    <T>(setter: (value: T) => void) =>
    (value: T) => {
      dirty.current = true;
      setter(value);
      setError('');
    };
  const validate = () => {
    let issue = '';
    if (enabled) {
      if (isLoading) issue = 'Настройки Outlook ещё загружаются. Дождитесь окончания загрузки.';
      else if (loadError || !saved || saved.eventId !== eventId)
        issue = 'Не удалось загрузить настройки Outlook. Обновите страницу и попробуйте ещё раз.';
      else if (!readOnly && (!date || Number.isNaN(date.getTime()) || !start || !end || start.value >= end.value)) {
        issue = 'Укажите корректные начало и окончание встречи Outlook. Окончание должно быть позже начала.';
      }
    }
    setError(issue);
    return !issue;
  };
  const buildSettings = () =>
    enabled && !readOnly && date && start && end
      ? {
          calendarMeetingDateStart: formatLocalDateToUtc(date, `T${start.value}:00`),
          calendarMeetingDateEnd: formatLocalDateToUtc(date, `T${end.value}:00`),
          calendarMeetingPoint: location,
          calendarMeetingDescription: comment,
        }
      : undefined;
  const toggle = useCallback(() => {
    setEnabled((value) => !value);
    setError('');
  }, []);

  return {
    enabled,
    readOnly,
    isLoading,
    error,
    toggle,
    validate,
    buildSettings,
    values: {
      eventMeetingDate: date,
      outlookMeetingStartTime: start,
      outlookMeetingEndTime: end,
      outlookMeetingLocationOrLink: location,
      outlookMeetingComment: comment,
    },
    actions: {
      onOutlookMeetingStartTimeChange: onChange(setStart),
      onOutlookMeetingEndTimeChange: onChange(setEnd),
      onOutlookMeetingLocationOrLinkChange: onChange(setLocation),
      onOutlookMeetingCommentChange: onChange(setComment),
    },
  };
};
